/**
 * TWAP execution policy — the pure half of the policy form (TASK-20260823-twap-strategies-tca, PR-A2).
 *
 * One object shape serves the manual ticket (TradingPanel) today and the strategy deploy form (PR-B).
 * The wire form is `DeployStrategyRequest.Twap` (spec AC 11): the same names camelCased, symbols as
 * a CSV, ladder postures as the C# `PricingStrategy` shorts. Bounds mirror the server validator so a
 * violation is named inline before the request is ever sent — the server still enforces them (400
 * naming the field); the client mirror is a convenience, never the guard.
 */

/** C# `PricingStrategy` enum (Data.Models/OrderBookModel.cs) — the short stored on every child row. */
export const POSTURES = Object.freeze({
  MatchMarketSize: 0,        // most aggressive: the price that fills the whole size from the book
  BetterThanMarketSize: 1,   // a tick inside the market-for-size price
  ScaleByOrderBookDepth: 2,  // walk the book to the configured depth
  ScaleByTick: 3,            // step by ticks
  MatchBestLimitPrice: 4,    // least aggressive: match the best bid/ask/mid/trade
});

/** Posture short → name. */
export const POSTURE_NAMES = Object.freeze(
  Object.fromEntries(Object.entries(POSTURES).map(([name, short]) => [short, name])),
);

/** Human labels for the ladder editor / blotter chips. */
export const POSTURE_LABELS = Object.freeze({
  0: 'Match market size',
  1: 'Better than market size',
  2: 'Scale by book depth',
  3: 'Scale by tick',
  4: 'Match best price',
});

/** Server `PriceBasis` values (Bid / Ask / Mid / Trade). */
export const PRICE_BASES = Object.freeze(['Bid', 'Ask', 'Mid', 'Trade']);

/** The default posture ladder: `%time → posture`, ascending, ending at 101 (= "always" for the tail). */
export const DEFAULT_LADDER = Object.freeze([
  Object.freeze({ pctTime: 25, posture: POSTURES.MatchBestLimitPrice }),
  Object.freeze({ pctTime: 50, posture: POSTURES.ScaleByTick }),
  Object.freeze({ pctTime: 75, posture: POSTURES.ScaleByOrderBookDepth }),
  Object.freeze({ pctTime: 90, posture: POSTURES.BetterThanMarketSize }),
  Object.freeze({ pctTime: 101, posture: POSTURES.MatchMarketSize }),
]);

/** Inclusive [min, max] bounds — the single source the form's min/max attributes render from. */
export const POLICY_BOUNDS = Object.freeze({
  durationMinutes: [1, 1440],
  sliceIntervalSeconds: [30, 3600],
  slippageCapPct: [0, 10],
  maxConcurrentParents: [1, Number.POSITIVE_INFINITY], // the host ceiling is the server's to enforce
  maxBookDepth: [1, 50],
  tickStepSize: [1, 20],
  reverseTickStepSize: [1, 20],
  thresholdForAggressivePricing: [-20, 20],
  thresholdForConservativePricing: [-20, 20],
});

/** Matches the C# defaults (spec AC 11). Frozen: copy it into form state, never mutate it. */
export const DEFAULT_POLICY = Object.freeze({
  enabled: false,
  durationMinutes: 30,
  symbols: Object.freeze([]),           // allow-list ⊆ deployment symbols; empty = all
  sliceIntervalSeconds: 60,
  slippageCapPct: 0,                    // 0 = off
  maxConcurrentParents: 20,
  priceBasis: 'Mid',
  maxBookDepth: 5,
  doNotTradeAheadOfSchedule: true,
  tickStepSize: 1,
  reverseTickStepSize: 1,
  thresholdForAggressivePricing: -2,
  thresholdForConservativePricing: 2,
  pricingStrategySequence: DEFAULT_LADDER,
});

const isInt = (v) => typeof v === 'number' && Number.isInteger(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function inRange(v, [lo, hi]) {
  return isNum(v) && v >= lo && v <= hi;
}

function rangeText([lo, hi]) {
  return Number.isFinite(hi) ? `${lo}–${hi}` : `at least ${lo}`;
}

function intError(field, label, v, errors) {
  const bounds = POLICY_BOUNDS[field];
  if (!isNum(v) || !inRange(v, bounds)) errors[field] = `${label} must be ${rangeText(bounds)}`;
  else if (!isInt(v)) errors[field] = `${label} must be a whole number`;
}

function validateLadder(seq) {
  if (!Array.isArray(seq) || seq.length === 0) return 'Ladder needs at least one step';
  let prev = -Infinity;
  for (const step of seq) {
    if (!step || !isNum(step.pctTime) || !isInt(step.posture) || POSTURE_NAMES[step.posture] === undefined) {
      return 'Each ladder step needs a %time and a known posture';
    }
    if (step.pctTime <= prev) return 'Ladder %time must be strictly ascending';
    prev = step.pctTime;
  }
  if (seq[seq.length - 1].pctTime !== 101) return 'Ladder must end at 101 % (the catch-all step)';
  return null;
}

/**
 * Validate a policy against the server bounds. `ctx.symbols`, when given, is the deployment's symbol
 * list the allow-list must be a subset of. Returns `{ ok, errors, warnings }` keyed by field; a
 * disabled policy is always ok (the server ignores every other member when `enabled` is false).
 */
export function validatePolicy(p, ctx = {}) {
  const errors = {};
  const warnings = {};
  if (!p || !p.enabled) return { ok: true, errors, warnings };

  intError('durationMinutes', 'Duration', p.durationMinutes, errors);
  intError('sliceIntervalSeconds', 'Slice interval', p.sliceIntervalSeconds, errors);
  if (!errors.sliceIntervalSeconds && !errors.durationMinutes && p.sliceIntervalSeconds > p.durationMinutes * 60) {
    errors.sliceIntervalSeconds = 'Slice interval cannot exceed the duration';
  }
  if (!inRange(p.slippageCapPct, POLICY_BOUNDS.slippageCapPct)) {
    errors.slippageCapPct = `Slippage cap must be ${rangeText(POLICY_BOUNDS.slippageCapPct)} %`;
  }
  intError('maxConcurrentParents', 'Max concurrent parents', p.maxConcurrentParents, errors);
  if (!PRICE_BASES.includes(p.priceBasis)) errors.priceBasis = `Price basis must be one of ${PRICE_BASES.join(', ')}`;
  intError('maxBookDepth', 'Book depth', p.maxBookDepth, errors);
  intError('tickStepSize', 'Tick step', p.tickStepSize, errors);
  intError('reverseTickStepSize', 'Reverse tick step', p.reverseTickStepSize, errors);
  intError('thresholdForAggressivePricing', 'Aggressive threshold', p.thresholdForAggressivePricing, errors);
  intError('thresholdForConservativePricing', 'Conservative threshold', p.thresholdForConservativePricing, errors);
  if (!errors.thresholdForAggressivePricing && !errors.thresholdForConservativePricing
      && p.thresholdForAggressivePricing >= p.thresholdForConservativePricing) {
    errors.thresholdForAggressivePricing = 'Aggressive threshold must be below the conservative threshold';
  }
  const ladderError = validateLadder(p.pricingStrategySequence);
  if (ladderError) errors.pricingStrategySequence = ladderError;

  if (Array.isArray(ctx.symbols) && Array.isArray(p.symbols) && p.symbols.length > 0) {
    const allowed = new Set(ctx.symbols);
    const unknown = p.symbols.filter((s) => !allowed.has(s));
    if (unknown.length > 0) errors.symbols = `Not deployment symbols: ${unknown.join(', ')}`;
  }

  if (p.doNotTradeAheadOfSchedule === false) {
    warnings.doNotTradeAheadOfSchedule = 'Trading ahead of schedule: a fill can trigger the next slice immediately, so the child-order rate rests on the concurrent-parent caps alone';
  }

  return { ok: Object.keys(errors).length === 0, errors, warnings };
}

/** Number of slices the schedule plans: ⌈duration / interval⌉, or null when unknowable. */
export function sliceCount(p) {
  if (!p || !isNum(p.durationMinutes) || !isNum(p.sliceIntervalSeconds) || p.sliceIntervalSeconds <= 0) return null;
  return Math.ceil((p.durationMinutes * 60) / p.sliceIntervalSeconds);
}

/** Policy → `DeployStrategyRequest.Twap` wire shape; null when the policy is off. */
export function policyToRequest(p) {
  if (!p || !p.enabled) return null;
  const symbols = Array.isArray(p.symbols) && p.symbols.length > 0 ? p.symbols.join(',') : null;
  // contract: Data.Models/Twap/TwapExecutionPolicy.cs — the core knobs at the top level, the advanced
  // pricing surface NESTED under `pricing` (TwapPricingOptions). The same shape is the deploy request's
  // `twap` (PR-B) and the manual order body (`POST /api/Order` → ManualOrderRequest.Twap), so ONE shape.
  return {
    enabled: true,
    durationMinutes: p.durationMinutes,
    symbols,
    sliceIntervalSeconds: p.sliceIntervalSeconds,
    slippageCapPct: p.slippageCapPct,
    maxConcurrentParents: p.maxConcurrentParents,
    pricing: {
      priceBasis: p.priceBasis,
      maxBookDepth: p.maxBookDepth,
      doNotTradeAheadOfSchedule: p.doNotTradeAheadOfSchedule,
      tickStepSize: p.tickStepSize,
      reverseTickStepSize: p.reverseTickStepSize,
      thresholdForAggressivePricing: p.thresholdForAggressivePricing,
      thresholdForConservativePricing: p.thresholdForConservativePricing,
      pricingStrategySequence: (p.pricingStrategySequence ?? []).map((s) => ({ pctTime: s.pctTime, posture: s.posture })),
    },
  };
}

function postureShort(v) {
  if (isInt(v)) return v;
  if (typeof v === 'string' && POSTURES[v] !== undefined) return POSTURES[v];
  return null;
}

function parseSymbols(v) {
  if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

/** Wire shape → policy; every null/missing member takes the default (the server's `Effective*()` rule). */
export function requestToPolicy(req) {
  if (!req) return DEFAULT_POLICY;
  // The pricing knobs live under `pricing` (TwapPricingOptions); a flat request is tolerated on read
  // so a hand-built object (e.g. the report's policy view) can pass the knobs at the top level.
  const pricing = req.pricing && typeof req.pricing === 'object' ? req.pricing : req;
  const pick = (src, k) => (src[k] === null || src[k] === undefined ? DEFAULT_POLICY[k] : src[k]);
  const ladder = Array.isArray(pricing.pricingStrategySequence) && pricing.pricingStrategySequence.length > 0
    ? pricing.pricingStrategySequence.map((s) => ({ pctTime: s.pctTime, posture: postureShort(s.posture) }))
    : DEFAULT_LADDER.map((s) => ({ ...s }));
  return {
    enabled: req.enabled === true,
    durationMinutes: pick(req, 'durationMinutes'),
    symbols: parseSymbols(req.symbols),
    sliceIntervalSeconds: pick(req, 'sliceIntervalSeconds'),
    slippageCapPct: pick(req, 'slippageCapPct'),
    maxConcurrentParents: pick(req, 'maxConcurrentParents'),
    priceBasis: pick(pricing, 'priceBasis'),
    maxBookDepth: pick(pricing, 'maxBookDepth'),
    doNotTradeAheadOfSchedule: pick(pricing, 'doNotTradeAheadOfSchedule'),
    tickStepSize: pick(pricing, 'tickStepSize'),
    reverseTickStepSize: pick(pricing, 'reverseTickStepSize'),
    thresholdForAggressivePricing: pick(pricing, 'thresholdForAggressivePricing'),
    thresholdForConservativePricing: pick(pricing, 'thresholdForConservativePricing'),
    pricingStrategySequence: ladder,
  };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** `{ field: { from, to } }` for every member that differs (arrays compared by value). */
export function diffPolicy(a, b) {
  const out = {};
  for (const key of Object.keys(DEFAULT_POLICY)) {
    const from = a?.[key];
    const to = b?.[key];
    if (!same(from, to)) out[key] = { from, to };
  }
  return out;
}

/**
 * The `ParamsUpdateRequest.twap` value for a Parameters-tab save (PR-B, spec AC 12): `undefined` when
 * nothing changed (the field is left off the patch — "unchanged"), `{ enabled: false }` when the
 * operator switched TWAP off, else the full wire policy — the server REPLACES the whole stored policy
 * (`OrgAdjustmentService.UpdateParamsAsync`), so a partial object would silently reset the other knobs.
 */
export function policyPatch(baseline, edited) {
  if (Object.keys(diffPolicy(baseline, edited)).length === 0) return undefined;
  if (!edited?.enabled) return baseline?.enabled ? { enabled: false } : undefined;
  return policyToRequest(edited);
}

/**
 * Keep the allow-list inside `symbols` (the form's current symbol list): a symbol removed from the
 * deployment must leave the allow-list too, or the server refuses the policy as "not deployed".
 * Returns the same object when nothing had to go, so a reactive statement does not loop.
 */
export function restrictSymbols(policy, symbols) {
  if (!policy || !Array.isArray(policy.symbols) || policy.symbols.length === 0) return policy;
  const allowed = new Set(Array.isArray(symbols) ? symbols : []);
  const kept = policy.symbols.filter((s) => allowed.has(s));
  return kept.length === policy.symbols.length ? policy : { ...policy, symbols: kept };
}

const BASIS_WORD = { Bid: 'bid', Ask: 'ask', Mid: 'mid', Trade: 'trade' };

/** One-line summary, e.g. `30 min · 30 slices × 60 s · mid-based · cap 0.5 %`. */
export function describePolicy(p) {
  if (!p || !p.enabled) return 'TWAP off';
  const slices = sliceCount(p);
  const cap = isNum(p.slippageCapPct) && p.slippageCapPct > 0 ? `cap ${p.slippageCapPct} %` : 'no cap';
  const basis = `${BASIS_WORD[p.priceBasis] ?? String(p.priceBasis).toLowerCase()}-based`;
  return `${p.durationMinutes} min · ${slices ?? '?'} slices × ${p.sliceIntervalSeconds} s · ${basis} · ${cap}`;
}
